<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import ArgonSelect from "@/components/ArgonSelect.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import DualListSelector from "./DualListSelector.vue";
import TaskCandidateCard from "./TaskCandidateCard.vue";
import { warehouseStore } from "@/_store/warehouseStore";
import { taskStore } from "@/_store/taskStore";
import { stopService } from "@/_services";
import { errorUtils } from "@/utils";

const props = defineProps({
  modalId: { type: String, required: true },
  routeId: { type: String, required: true },
  stopId: { type: String, default: "" },
});

const emit = defineEmits(["close", "confirm"]);

const isEditMode = computed(() => !!props.stopId);

const modalCreateLoadStop = ref(null);
const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const warehousesStore = warehouseStore();
const tasksStore = taskStore();

const snackbar = ref(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const allWarehouses = ref([]);
const candidates = ref([]);
const clientFilter = ref("");
const searchText = ref("");
const selectedTaskIds = ref([]);

const form = ref({
  warehouseId: "",
});

const rules = {
  warehouseId: {
    required: helpers.withMessage("El punto habitual es obligatorio", required),
  },
};
const v$ = useVuelidate(rules, form);

const warehouseOptions = computed(() =>
  allWarehouses.value.filter((warehouse) => warehouse?.type === "PROPIO")
);

const clientOptions = computed(() => {
  const clients = [
    ...new Set(
      candidates.value.map((task) => String(task?.contact_name || "").trim()).filter(Boolean)
    ),
  ];
  return clients.map((client) => ({ value: client, label: client }));
});

const searchFilteredCandidates = computed(() => {
  const search = searchText.value.trim().toLowerCase();
  return candidates.value.filter((task) => {
    if (clientFilter.value && task?.contact_name !== clientFilter.value) return false;
    if (!search) return true;
    return String(task?.barcode || "").toLowerCase().includes(search);
  });
});

const leftItems = computed(() =>
  searchFilteredCandidates.value.filter(
    (task) => !selectedTaskIds.value.includes(String(task?._id || ""))
  )
);

const rightItems = computed(() =>
  candidates.value.filter((task) =>
    selectedTaskIds.value.includes(String(task?._id || ""))
  )
);

const hasSelection = computed(() => selectedTaskIds.value.length > 0);

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo cargar la información.",
    type: "danger",
    color: "white",
  };
}

function extractListFromResponse(response) {
  const source = response?.data || response || {};
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.data)) return source.data;
  return [];
}

async function loadCandidates() {
  isLoading.value = true;
  closeSnackbar();
  try {
    const response = await tasksStore.getLoadCandidates(props.routeId);
    const loadedCandidates = extractListFromResponse(response);
    let editingTaskIds = [];

    if (isEditMode.value) {
      const stopResponse = await stopService.getStopById(props.stopId);
      const stopData = stopResponse?.data || stopResponse || {};
      const referencedTasks = Array.isArray(stopData.tasks) ? stopData.tasks : [];
      form.value.warehouseId = String(stopData.warehouse_id || "");

      const candidateIds = new Set(loadedCandidates.map((task) => String(task?._id || "")));
      for (const task of referencedTasks) {
        const id = String(task?._id || "");
        if (id && !candidateIds.has(id)) {
          loadedCandidates.push(task);
          candidateIds.add(id);
        }
      }
      editingTaskIds = referencedTasks.map((task) => String(task?._id || "")).filter(Boolean);
    }

    candidates.value = loadedCandidates;
    selectedTaskIds.value = editingTaskIds;
  } catch (error) {
    showError(
      errorUtils.getErrorMessageHtml(
        error,
        "No se pudieron cargar los bultos pendientes de carga."
      )
    );
    candidates.value = [];
  } finally {
    isLoading.value = false;
  }
}

function selectTask(taskId) {
  const id = String(taskId || "");
  if (!id || selectedTaskIds.value.includes(id)) return;
  selectedTaskIds.value = [...selectedTaskIds.value, id];
}

function unselectTask(taskId) {
  const id = String(taskId || "");
  selectedTaskIds.value = selectedTaskIds.value.filter((existing) => existing !== id);
}

function selectAllVisible() {
  const visibleIds = leftItems.value.map((item) => String(item?._id || "")).filter(Boolean);
  selectedTaskIds.value = [...new Set([...selectedTaskIds.value, ...visibleIds])];
}

function unselectAll() {
  selectedTaskIds.value = [];
}

function handleHide() {
  isSubmitting.value = false;
  v$.value.$reset();
  emit("close");
}

async function confirmCreate() {
  if (isLoading.value || isSubmitting.value) return;
  await v$.value.$validate();
  if (v$.value.$errors.length > 0) {
    showError(v$.value.$errors[0].$message);
    return;
  }
  if (!hasSelection.value) {
    showError("Selecciona al menos un bulto.");
    return;
  }

  isSubmitting.value = true;
  closeSnackbar();
  emit("confirm", {
    warehouseId: form.value.warehouseId,
    taskIds: [...selectedTaskIds.value],
    stopId: props.stopId || null,
  });
  modalCreateLoadStop.value?.closeModal();
}

watch(
  () => warehousesStore.warehouses,
  (value) => {
    allWarehouses.value = Array.isArray(value?.data) ? value.data : [];
  }
);

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById(props.modalId), {
    keyboard: true,
    backdrop: true,
  });
  modal.show();
  warehousesStore.getWarehouses({ page: 1, limit: 500 }, {}, { _id: -1 });
  loadCandidates();
});
</script>

<template>
  <modal-base
    :id="modalId"
    ref="modalCreateLoadStop"
    width="60rem"
    @hide="handleHide"
  >
    <template #header>
      <h6 class="mb-0">{{ isEditMode ? "Modificar parada de carga" : "Parada de carga" }}</h6>
    </template>
    <template #body>
      <alert-message title="Aviso" type="info" class="mb-3">
        Se recomienda añadir las paradas de carga después de optimizar el plan: al optimizar se puede cambiar su posición en la ruta.
      </alert-message>
      <div class="row modal-form-row">
        <div class="modal-form-label">
          <label for="load-stop-warehouse" class="col-form-label required"
            >Carga</label
          >
        </div>
        <div class="col">
          <argon-select
            id="load-stop-warehouse"
            v-model="form.warehouseId"
            name-value="_id"
            name-label="name"
            :options="warehouseOptions"
            :error="v$.warehouseId.$error"
            :is-required="true"
            placeholder="Selecciona el punto habitual de carga"
            filterable
          />
        </div>
      </div>

      <dual-list-selector
        :left-items="leftItems"
        :right-items="rightItems"
        :loading="isLoading"
        left-empty-message="No hay bultos pendientes de carga"
        right-empty-message="Ningún bulto seleccionado"
        @select="(item) => selectTask(item._id)"
        @unselect="(item) => unselectTask(item._id)"
        @select-all="selectAllVisible"
        @unselect-all="unselectAll"
      >
        <template #filters>
          <argon-input
            id="load-stop-search"
            v-model="searchText"
            placeholder="Bulto"
            class="dual-list-filters-search"
          />
          <argon-select
            id="load-stop-client"
            v-model="clientFilter"
            name-value="value"
            name-label="label"
            :options="clientOptions"
            placeholder="Contacto"
            clearable
            class="dual-list-filters-client"
          />
        </template>
        <template #item="{ item }">
          <task-candidate-card :item="item" :show-courier="false" />
        </template>
      </dual-list-selector>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="isSubmitting ? 'Guardando...' : (isEditMode ? 'Guardar cambios' : 'Crear parada')"
        :loading="isSubmitting"
        :disable-button="isLoading || isSubmitting"
        @click="confirmCreate"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>

<style scoped lang="scss">
.dual-list-filters-search {
  flex: 1 1 auto;
  min-width: 0;
}

.dual-list-filters-search :deep(.form-group),
.dual-list-filters-client :deep(.choices) {
  margin-bottom: 0;
}

.dual-list-filters-client {
  flex: 0 0 10rem;
}

</style>
